<template>
  <div class="min-h-screen flex flex-col">

    <!-- ── Top bar ── -->
    <nav class="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-md border-b border-white/10">
      <div class="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z"/>
              <circle cx="7.5" cy="14.5" r="1.5"/>
              <circle cx="16.5" cy="14.5" r="1.5"/>
            </svg>
          </div>
          <span class="text-white font-bold text-lg tracking-tight">CabBook</span>
        </div>
        <div class="flex items-center gap-3">
          <router-link
            to="/login"
            class="text-slate-300 hover:text-white text-sm font-medium transition-colors px-3 py-1.5"
          >
            Login
          </router-link>
          <router-link
            to="/register"
            class="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            Get started
          </router-link>
        </div>
      </div>
    </nav>

    <!-- ── Hero ── -->
    <section class="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex items-center min-h-[80vh]">
      <!-- Decorative blobs -->
      <div class="pointer-events-none absolute -top-48 -right-48 w-[700px] h-[700px] rounded-full bg-blue-600/10"></div>
      <div class="pointer-events-none absolute top-16 right-24 w-72 h-72 rounded-full bg-indigo-500/10"></div>
      <div class="pointer-events-none absolute -bottom-40 -left-24 w-[500px] h-[500px] rounded-full bg-blue-800/15"></div>
      <!-- Grid texture -->
      <div class="pointer-events-none absolute inset-0 opacity-[0.03]" style="background-image: linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px); background-size: 60px 60px;"></div>

      <div class="relative max-w-7xl mx-auto px-8 py-24 w-full">
        <div class="max-w-3xl">
          <!-- Badge -->
          <div class="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-10">
            <span class="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span>
            Platform live and ready
          </div>

          <h1 class="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight mb-7">
            Book your cab.<br/>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
              Anytime, anywhere.
            </span>
          </h1>

          <p class="text-lg sm:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl">
            Fast, reliable cab booking with real-time notifications, weather-aware location management, and instant card payments — all in one place.
          </p>

          <div class="flex items-center gap-4 flex-wrap">
            <router-link
              to="/register"
              class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-xl text-base transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
            >
              Get started free
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
            <router-link
              to="/login"
              class="inline-flex items-center gap-1.5 text-slate-300 hover:text-white font-medium text-base transition-colors"
            >
              Already have an account
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Features ── -->
    <section class="bg-white py-24">
      <div class="max-w-7xl mx-auto px-8">
        <div class="text-center mb-16">
          <p class="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-3">What we offer</p>
          <h2 class="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Everything you need for the perfect ride</h2>
          <p class="text-slate-500 text-lg max-w-xl mx-auto">A full platform built for passengers — from booking to payment and beyond.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Bookings -->
          <div class="bg-slate-50 rounded-2xl p-7 border border-slate-100 hover:border-blue-100 hover:shadow-md transition-all duration-200">
            <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z"/>
                <circle cx="7.5" cy="14.5" r="1.5"/>
                <circle cx="16.5" cy="14.5" r="1.5"/>
              </svg>
            </div>
            <h3 class="text-base font-bold text-slate-800 mb-2">Ride Bookings</h3>
            <p class="text-sm text-slate-500 leading-relaxed">Choose your cab type, set pickup and drop-off coordinates, and create bookings in seconds.</p>
          </div>

          <!-- Locations -->
          <div class="bg-slate-50 rounded-2xl p-7 border border-slate-100 hover:border-emerald-100 hover:shadow-md transition-all duration-200">
            <div class="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <h3 class="text-base font-bold text-slate-800 mb-2">Smart Locations</h3>
            <p class="text-sm text-slate-500 leading-relaxed">Save your favourite pickup spots and check live weather forecasts for any of your saved addresses.</p>
          </div>

          <!-- Payments -->
          <div class="bg-slate-50 rounded-2xl p-7 border border-slate-100 hover:border-sky-100 hover:shadow-md transition-all duration-200">
            <div class="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
              </svg>
            </div>
            <h3 class="text-base font-bold text-slate-800 mb-2">Instant Payments</h3>
            <p class="text-sm text-slate-500 leading-relaxed">Pay for completed rides instantly by card. Discount support included — great savings when available.</p>
          </div>

          <!-- Notifications -->
          <div class="bg-slate-50 rounded-2xl p-7 border border-slate-100 hover:border-violet-100 hover:shadow-md transition-all duration-200">
            <div class="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
            </div>
            <h3 class="text-base font-bold text-slate-800 mb-2">Live Notifications</h3>
            <p class="text-sm text-slate-500 leading-relaxed">Get notified the moment your cab is ready, and receive automatic alerts when discounts are available.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CTA Banner ── -->
    <section class="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 py-20 text-white text-center">
      <div class="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5"></div>
      <div class="pointer-events-none absolute -bottom-20 -left-16 w-64 h-64 rounded-full bg-blue-500/20"></div>
      <div class="relative max-w-xl mx-auto px-8">
        <h2 class="text-3xl sm:text-4xl font-bold mb-4">Ready to get moving?</h2>
        <p class="text-blue-200 text-lg mb-10">Create a free account and book your first cab in minutes.</p>
        <router-link
          to="/register"
          class="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-8 py-4 rounded-xl text-base hover:bg-blue-50 transition-colors hover:shadow-xl hover:-translate-y-0.5"
        >
          Create your account
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </router-link>
      </div>
    </section>

    <!-- ── Footer ── -->
    <footer class="bg-slate-950 text-slate-600 text-sm py-8">
      <div class="max-w-7xl mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="flex items-center gap-2.5">
          <div class="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
            <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z"/>
            </svg>
          </div>
          <span class="text-slate-400 font-semibold">CabBook</span>
        </div>
        <p class="text-slate-600">Distributed Programming Assignment</p>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: "HomeView",
};
</script>
